import * as dagre from "dagre"

import type { Edge, Node } from "@xyflow/react"

type LayoutDirection = "LR" | "TB"

type LayoutOptions = {
  direction?: LayoutDirection
  nodeSpacing?: number
  rankSpacing?: number
}

const DEFAULT_NODE_WIDTH = 250
const DEFAULT_NODE_HEIGHT = 150
const DEFAULT_NODE_SPACING = 75
const DEFAULT_RANK_SPACING = 100
const CONTAINER_HEADER_HEIGHT = 160 // header (~40px) + settings panel (~120px)
const CONTAINER_PADDING = 20

export function layoutWorkflowGraph(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): { nodes: Node[]; edges: Edge[] } {
  if (nodes.length === 0) {
    return { nodes: [], edges }
  }

  const {
    direction = "LR",
    nodeSpacing = DEFAULT_NODE_SPACING,
    rankSpacing = DEFAULT_RANK_SPACING,
  } = options

  // Separate parent (container) nodes from child nodes
  const parentNodes = nodes.filter((n) => n.type === "loopContainer")
  const childNodesByParent = new Map<string, Node[]>()
  const topLevelNodes: Node[] = []

  for (const node of nodes) {
    if (node.parentId) {
      const children = childNodesByParent.get(node.parentId) || []
      children.push(node)
      childNodesByParent.set(node.parentId, children)
    } else if (node.type !== "loopContainer") {
      topLevelNodes.push(node)
    }
  }

  // Layout children within each container first
  const layoutedChildrenByParent = new Map<string, Node[]>()

  for (const parent of parentNodes) {
    const children = childNodesByParent.get(parent.id) || []
    if (children.length > 0) {
      // Get edges that connect children within this container
      const childIds = new Set(children.map((c) => c.id))
      const childEdges = edges.filter((e) => childIds.has(e.source) && childIds.has(e.target))

      // Layout children
      const childDagreGraph = new dagre.graphlib.Graph()
      childDagreGraph.setDefaultEdgeLabel(() => ({}))
      childDagreGraph.setGraph({
        rankdir: direction,
        nodesep: nodeSpacing / 2,
        ranksep: rankSpacing / 2,
        marginx: CONTAINER_PADDING,
        marginy: CONTAINER_PADDING,
      })

      for (const child of children) {
        const width = child.measured?.width ?? child.width ?? DEFAULT_NODE_WIDTH
        const height = child.measured?.height ?? child.height ?? DEFAULT_NODE_HEIGHT
        childDagreGraph.setNode(child.id, { width, height })
      }

      for (const edge of childEdges) {
        childDagreGraph.setEdge(edge.source, edge.target)
      }

      dagre.layout(childDagreGraph)

      // Calculate bounds to resize container if needed
      let maxX = 0
      let maxY = 0

      const layoutedChildren = children.map((child) => {
        const nodeWithPosition = childDagreGraph.node(child.id)
        const width = nodeWithPosition.width
        const height = nodeWithPosition.height
        // Position relative to container, accounting for header
        const positionX = nodeWithPosition.x - width / 2 + CONTAINER_PADDING
        const positionY =
          nodeWithPosition.y - height / 2 + CONTAINER_HEADER_HEIGHT + CONTAINER_PADDING

        maxX = Math.max(maxX, positionX + width + CONTAINER_PADDING)
        maxY = Math.max(maxY, positionY + height + CONTAINER_PADDING)

        return {
          ...child,
          position: { x: positionX, y: positionY },
        }
      })

      layoutedChildrenByParent.set(parent.id, layoutedChildren)

      // Update parent container size if children exceed current bounds
      const currentWidth = (parent.data.width as number) || 500
      const currentHeight = (parent.data.height as number) || 350
      if (maxX > currentWidth || maxY > currentHeight) {
        parent.data = {
          ...parent.data,
          width: Math.max(currentWidth, maxX),
          height: Math.max(currentHeight, maxY),
        }
      }
    }
  }

  // Now layout top-level nodes (including containers as single units)
  const allTopLevel = [...topLevelNodes, ...parentNodes]

  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: nodeSpacing,
    ranksep: rankSpacing,
    marginx: 20,
    marginy: 20,
  })

  for (const node of allTopLevel) {
    const width =
      node.type === "loopContainer"
        ? (node.data.width as number) || 500
        : (node.measured?.width ?? node.width ?? DEFAULT_NODE_WIDTH)
    const height =
      node.type === "loopContainer"
        ? (node.data.height as number) || 350
        : (node.measured?.height ?? node.height ?? DEFAULT_NODE_HEIGHT)
    dagreGraph.setNode(node.id, { width, height })
  }

  // Add edges for top-level nodes (edges from/to containers, not their children)
  const topLevelIds = new Set(allTopLevel.map((n) => n.id))
  for (const edge of edges) {
    if (topLevelIds.has(edge.source) && topLevelIds.has(edge.target)) {
      dagreGraph.setEdge(edge.source, edge.target)
    }
  }

  dagre.layout(dagreGraph)

  let minX = Infinity
  let minY = Infinity
  for (const nodeId of dagreGraph.nodes()) {
    const nodeWithPosition = dagreGraph.node(nodeId)
    minX = Math.min(minX, nodeWithPosition.x - nodeWithPosition.width / 2)
    minY = Math.min(minY, nodeWithPosition.y - nodeWithPosition.height / 2)
  }

  const offsetX = minX < 0 ? -minX : 0
  const offsetY = minY < 0 ? -minY : 0

  // Build final nodes array
  const layoutedNodes: Node[] = []

  for (const node of allTopLevel) {
    const nodeWithPosition = dagreGraph.node(node.id)
    const width = nodeWithPosition.width
    const height = nodeWithPosition.height
    const positionX = nodeWithPosition.x - width / 2 + offsetX
    const positionY = nodeWithPosition.y - height / 2 + offsetY

    layoutedNodes.push({
      ...node,
      position: { x: positionX, y: positionY },
    })

    // Add layouted children for this container
    const layoutedChildren = layoutedChildrenByParent.get(node.id)
    if (layoutedChildren) {
      layoutedNodes.push(...layoutedChildren)
    }
  }

  return { nodes: layoutedNodes, edges }
}

export function hasTopologyChanges(operations: Array<{ type: string }>): boolean {
  const topologyTypes = new Set(["add_node", "remove_node", "add_edge", "remove_edge"])
  return operations.some((op) => topologyTypes.has(op.type))
}
